import { t } from '@lingui/macro';

// Template header row. Keep in sync with Contacts::BulkCsvImporter.
export const TEMPLATE_HEADERS = [
  'Account ID*',
  'FMS ID*',
  'First Name*',
  'Last Name*',
  'Email*',
  'Phone Number',
  'Address',
  'Address 2',
  'State',
  'City',
  'Postal Code',
  'Country',
  'Is Primary Contact',
  'Notes',
];

export const TEMPLATE_FILENAME = 'add_contacts_template.csv';

// UX-only limits; keep aligned with the API.
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_DATA_ROWS = 50_000;

export const TEMPLATE_SAMPLE_ROWS = [
  'DEMOFLEET1,Demo1,Jane ,Smith,Jane.Smith@email.com,1-111-222-3333,,,,,,,yes,Prefers morning appointments',
  'DEMOFLEET1,Demo2,John ,Doe,john.doe@email.com,1-444-333-2222,,,,,,,no,',
  'DEMOFLEET1,Demo3,Jill,Noel,,1-999-888-9999,,,,,,,yes,',
  'DEMOFLEET1,Demo4,Joe,Smith,joe.smith@email.com,,,,,,,,no,',
];

export const downloadContactTemplate = () => {
  const header = TEMPLATE_HEADERS.join(',');
  const rows = [header, ...TEMPLATE_SAMPLE_ROWS].join('\n');
  const blob = new Blob([`${rows}\n`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = TEMPLATE_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// FileReader, not Blob.text(), so it also works under jsdom.
const readText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

// that contain commas, newlines, or escaped quotes.
const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let fields: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && char === ',') {
      fields.push(field);
      field = '';
    } else if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      fields.push(field);
      rows.push(fields);
      fields = [];
      field = '';
    } else {
      field += char;
    }
  }

  // Handle last record if file doesn't end with a newline
  if (field !== '' || fields.length > 0) {
    fields.push(field);
    rows.push(fields);
  }

  return rows;
};

// A parsed row has data if any field contains non-whitespace.
const rowHasData = (fields: string[]) => fields.some((f) => f.trim() !== '');

const normalizeHeader = (header: string) =>
  header.replace(/\*/g, '').trim().toLowerCase();

// "Account" is the pre-PD-5352 header; the API still accepts it as an alias
// for "Account ID", so files built from the older template keep working.
const ACCOUNT_HEADERS = ['account id', 'account'];

// Returns a user-facing error message, or null when acceptable. Size is checked before
// reading so a huge file isn't loaded into memory.
export const validateContactCsv = async (
  file: File,
): Promise<string | null> => {
  const isCsv =
    file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
  if (!isCsv) return t`Please upload a .csv file.`;

  if (file.size > MAX_FILE_BYTES) {
    return t`The file exceeds the maximum size of 10 MB.`;
  }

  const allRows = parseCsvRows(await readText(file));
  const records = allRows.filter(rowHasData);
  if (records.length === 0) return t`The file is empty.`;

  const headers = records[0].map(normalizeHeader);
  if (!headers.some((header) => ACCOUNT_HEADERS.includes(header))) {
    return t`The file is missing the required Account ID column.`;
  }
  if (!headers.includes('fms id')) {
    return t`The file is missing the required FMS ID column.`;
  }

  const fmsIdIndex = headers.indexOf('fms id');

  const dataRows = records.length - 1;
  if (dataRows === 0) return t`The file has no contact rows.`;
  if (dataRows > MAX_DATA_ROWS) {
    return t`The file exceeds the maximum of 50,000 rows.`;
  }

  // Check that every data row has a non-empty FMS ID value.
  const dataRecords = records.slice(1);
  const hasEmptyFmsId = dataRecords.some((fields) => {
    const fmsValue = (fields[fmsIdIndex] || '').trim();
    return fmsValue === '';
  });

  if (hasEmptyFmsId) {
    return t`One or more rows are missing an FMS ID. Every contact must have an FMS ID.`;
  }

  return null;
};
